import { Box, Stack } from '@mui/joy'
import { useQueryClient } from '@tanstack/react-query'
import { useEvent, useGate, useStore } from 'effector-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  CashModal,
  cashModalModel,
  debtModel,
  DepositRepaymentModal,
  depositRepaymentModalModel,
} from '~/entities/Debt'
import { useDepositBalance, usePermissions } from '~/entities/viewer'
import {
  Status,
  Erroneous,
  ViewDebt,
  debtViewModel,
  CASH_DEPOSIT_GRID_KEY,
  DEPOSIT_REPAYMENT_GRID_KEY,
} from '~/features/Debt/ViewDebt'
import { DebtSourceTypesEnum, DebtStatusesEnum } from '~/shared/config/enums'
import { formatDateTimeForUI } from '~/shared/lib/date'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Button } from '~/shared/ui/Buttons'
import { UploadWhiteIcon } from '~/shared/ui/Icons'
import { Container, PageContainer, PageTitle } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import { QuickNavigation } from '~/shared/ui/QuickNavigation'
import { QuickNavigationOptions } from '~/shared/ui/QuickNavigation/QuickNavigation'
import { AuditsModal } from '~/widgets/AuditsModal'

export function ViewDebtPage() {
  const { t } = useTranslation()
  const { canDebtsUpdate, canAuditsView, canDebtsRepayFromDeposit } =
    usePermissions()
  const { isDepositBalanceEnabled } = useDepositBalance()

  const params = useParams<{ debtId: string }>()
  const debtId = params.debtId as string
  useGate(debtViewModel.Gate, { debtId })

  const queryClient = useQueryClient()

  const isDebtLoading = useStore(debtModel.requestFx.pending)
  const debt = useStore(debtViewModel.$debt)
  const deptError = debtModel.useDebtError(debtId)
  const status = debt?.getStatus()
  const sourceType = debt?.getSourceType()
  const isPaid = status === DebtStatusesEnum.PAID
  const isProcessing = status === DebtStatusesEnum.PROCESSING

  const inspectionDamages =
    sourceType === DebtSourceTypesEnum.INSPECTION_DAMAGES
  const inspectionViolations =
    sourceType === DebtSourceTypesEnum.INSPECTION_VIOLATIONS

  const title = useMemo(() => {
    const startedAt = debt?.getDate()

    return `${t('debt')}${
      startedAt ? ` ${t('from')} ${formatDateTimeForUI(startedAt)}` : ''
    }`
  }, [debt, t])

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [{ titleI18n: 'debts', link: '/debts' }, { title: title || '' }],
    [title],
  )

  const isCashModalOpen = useStore(cashModalModel.$isCashOpen)
  const openCashModal = useEvent(cashModalModel.openCash)
  const closeCashModal = useEvent(cashModalModel.closeCash)
  const isCashSubmitting = useStore(debtViewModel.cashDepositFx.pending)

  // Погашение с депозита: только при подключённом у таксопарка депозите, праве и статусе «В работе».
  // Остаток депозита запрашиваем у TMS, только когда кнопку в принципе можно показать.
  const canRepayFromDeposit =
    isDepositBalanceEnabled &&
    canDebtsRepayFromDeposit &&
    isProcessing &&
    !isDebtLoading

  const depositBalance = useStore(debtViewModel.$depositBalance)
  const isDepositBalanceLoading = useStore(
    debtViewModel.fetchDepositBalanceFx.pending,
  )
  const isDepositRepaymentModalOpen = useStore(
    depositRepaymentModalModel.$isDepositRepaymentOpen,
  )
  const openDepositRepaymentModal = useEvent(
    depositRepaymentModalModel.openDepositRepayment,
  )
  const closeDepositRepaymentModal = useEvent(
    depositRepaymentModalModel.closeDepositRepayment,
  )
  const isDepositRepaymentSubmitting = useStore(
    debtViewModel.depositRepaymentFx.pending,
  )

  useEffect(() => {
    if (canRepayFromDeposit) debtViewModel.depositBalanceRequested()
  }, [canRepayFromDeposit, debtId])

  const handleDepositRepaymentListUpdate = async () => {
    await queryClient.invalidateQueries({
      queryKey: [DEPOSIT_REPAYMENT_GRID_KEY],
    })
  }

  const handleDepositRepaymentSend = async (
    formValues: depositRepaymentModalModel.FormValues,
  ) => {
    debtViewModel.depositRepayment({
      formValues,
      fn: handleDepositRepaymentListUpdate,
    })
  }

  const handleCashDepositListUpdate = async () => {
    await queryClient.invalidateQueries({
      queryKey: [CASH_DEPOSIT_GRID_KEY],
    })
  }

  const quickNavigationOptions = [
    { labelI18n: 'enum.basic information', anchor: 'main' },
    { labelI18n: 'enum.payment', anchor: 'payment' },

    inspectionDamages || inspectionViolations
      ? { labelI18n: 'enum.detailing', anchor: 'detailing' }
      : undefined,

    { labelI18n: 'enum.cash list', anchor: 'cashList' },

    isDepositBalanceEnabled
      ? {
          labelI18n: 'enum.deposit repayments list',
          anchor: 'depositRepaymentList',
        }
      : undefined,
  ].filter(Boolean) as QuickNavigationOptions

  const handleCashSend = async (formValues: cashModalModel.FormValues) => {
    debtViewModel.cashDeposit({ formValues, fn: handleCashDepositListUpdate })
  }

  if (deptError) {
    return <NotFound message={t('alert.debt load error')} />
  }

  return (
    <PageContainer title={title}>
      <Container isHeader>
        <Stack direction='row' alignItems='end' justifyContent='space-between'>
          <Box>
            <Breadcrumbs list={breadcrumbs} lastLoading={isDebtLoading} />

            <Stack direction='row' alignItems='center' spacing={2}>
              <Status />
              {isPaid && <Erroneous />}
            </Stack>

            <PageTitle loading={isDebtLoading}>{title}</PageTitle>
          </Box>
          <Stack direction='row' spacing={2}>
            {canAuditsView && <AuditsModal auditableId={debtId} />}
            {canRepayFromDeposit && (
              <Button
                onClick={openDepositRepaymentModal}
                variant='brand'
                loading={isDepositBalanceLoading}
                disabled={!depositBalance || depositBalance <= 0}
                startDecorator={<UploadWhiteIcon />}
              >
                {t('button.repay from deposit')}
              </Button>
            )}
            {canDebtsUpdate && !isPaid && !isDebtLoading && (
              <Button
                onClick={openCashModal}
                variant='brand'
                startDecorator={<UploadWhiteIcon />}
              >
                {t('button.cash list')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Container>

      <ViewDebt debtId={debtId} />

      {!isDebtLoading && <QuickNavigation options={quickNavigationOptions} />}

      <CashModal
        isShow={isCashModalOpen}
        isSubmitting={isCashSubmitting}
        onCancel={closeCashModal}
        onOk={handleCashSend}
      />

      {canRepayFromDeposit && depositBalance !== null && (
        <DepositRepaymentModal
          isShow={isDepositRepaymentModalOpen}
          isSubmitting={isDepositRepaymentSubmitting}
          onCancel={closeDepositRepaymentModal}
          onOk={handleDepositRepaymentSend}
          depositBalance={depositBalance}
          debtBalance={debt?.getBalance() ?? 0}
        />
      )}
    </PageContainer>
  )
}
